import { Slice, Fragment, type Schema, type Node as PMNode } from '@tiptap/pm/model';
import { parseFromMarkdown, normalizeLogseqOutline } from '@lapo/core';

export interface MarkdownToSliceOptions {
  /** Journal/outline view: wrap blocks into list items so the bullet-list root invariant holds. */
  outline: boolean;
}

/**
 * When true, a plain paste (verbatim, no markdown parsing) is the DEFAULT paste behavior. Set once at
 * startup on Android, which has neither the desktop Ctrl/Cmd-Shift-V chord nor a native "Paste Plain"
 * menu item (see ios-overlay) — so its single native Paste is the plain-paste affordance. Desktop, web,
 * and iOS leave this false: they parse markdown on paste and offer plain paste separately.
 */
let plainPasteDefault = false;

/** Make plain (verbatim) paste the default paste behavior. Called once at app startup on Android. */
export function setPlainPasteDefault(on: boolean): void {
  plainPasteDefault = on;
}

/** Whether plain paste is the default (Android). Read by the editor's handlePaste. */
export function plainPasteIsDefault(): boolean {
  return plainPasteDefault;
}

/** Minimal ProseMirror-JSON node shape we post-process before rebinding to the editor schema. */
interface JSONNode {
  type: string;
  content?: JSONNode[];
  [k: string]: unknown;
}

/**
 * Drop the empty leading paragraph the canonical parser inserts into a list item whose real first
 * block isn't a paragraph (e.g. Logseq's `- # Heading` → `listItem[ p"", heading ]`). The editor's
 * `listItem` is `block+`, so a heading/quote/code can be the bullet's own line — the empty
 * paragraph would otherwise render as a stray blank bullet ("extra CR") before each heading.
 */
function dropEmptyLeadingParagraphs(node: JSONNode): JSONNode {
  if (!node.content) return node;
  node.content = node.content.map(dropEmptyLeadingParagraphs);
  if (node.type === 'listItem' && node.content.length > 1) {
    const first = node.content[0]!;
    if (first.type === 'paragraph' && (first.content?.length ?? 0) === 0) {
      node.content = node.content.slice(1);
    }
  }
  return node;
}

/**
 * Apply Logseq fold state: `collapsed[i]` (pre-order, from the normalizer) maps to the i-th list
 * item in document pre-order, so a block that was `collapsed:: true` in Logseq pastes in folded.
 */
function applyCollapsed(node: JSONNode, collapsed: boolean[], counter: { i: number }): void {
  if (node.type === 'listItem') {
    if (collapsed[counter.i]) node.attrs = { ...(node.attrs ?? {}), collapsed: true };
    counter.i += 1;
  }
  node.content?.forEach((child) => applyCollapsed(child, collapsed, counter));
}

/** True when the pasted text is one line (ignoring surrounding blank lines / a trailing newline). */
function isSingleLine(text: string): boolean {
  return !text.trim().includes('\n');
}

/**
 * The inline content of the parse's sole leaf paragraph, or null when the paste has any real block
 * structure (several lines, a heading/code block, a table's many cells, a lone image/rule). Backs the
 * single-line inline paste rule: one pasted line of plain/list/quote text inserts AT THE CARET —
 * marks kept, the leading `-`/`1.`/`>` marker already consumed by the parser — instead of splitting
 * the current line into a new bullet. The inverse of serializeClipboardMarkdown's single-line rule,
 * which drops the list indent when one leaf line is copied.
 */
function soleParagraphInline(blocks: PMNode[]): Fragment | null {
  const leaves: PMNode[] = [];
  const walk = (node: PMNode): void => {
    if (node.isTextblock) {
      leaves.push(node);
      return;
    }
    node.forEach(walk);
  };
  for (const block of blocks) walk(block);
  if (leaves.length !== 1) return null;
  const leaf = leaves[0]!;
  if (leaf.type.name !== 'paragraph' || leaf.content.size === 0) return null;
  return leaf.content;
}

/**
 * Turn pasted markdown into a ProseMirror slice ready for `replaceSelection`, in BOTH views.
 *
 * The canonical parser (`parseFromMarkdown`, @lapo/core) yields a doc in `lapoSchema`. The editor
 * runs a *separate* Schema instance generated from the same spec (lapoSchemaExtensions), so the
 * parsed nodes — though identical by name/attrs — belong to the wrong schema object and can't be
 * inserted directly. We rebind them by round-tripping through JSON into the editor's schema; this
 * is safe precisely because lapoSchemaExtensions.test.ts proves the two schemas are equivalent.
 *
 * - document mode (`outline:false`): insert the parsed blocks directly (open depth 0).
 * - outline mode (`outline:true`): every top-level block becomes a list item (a markdown list's
 *   own items are spliced in; a heading/quote/code block becomes a bullet's line, which the
 *   widened `listItem` content `block+` permits). The items are wrapped in a bullet list opened
 *   at depth 1 so they merge as siblings into the surrounding outline rather than nesting a list.
 */
export function markdownToSlice(
  markdown: string,
  schema: Schema,
  opts: MarkdownToSliceOptions,
): Slice {
  // Logseq exports are an outline, not CommonMark — normalize tab nesting, bare headings, and
  // `key:: value` properties first (a no-op for ordinary markdown), keeping the per-block fold
  // flags so collapsed Logseq blocks paste in collapsed.
  const { markdown: normalized, collapsed } = normalizeLogseqOutline(markdown);
  const parsed = parseFromMarkdown(normalized);
  // Rebind lapoSchema nodes → editor schema (names/attrs are equivalent; see test), after
  // dropping the parser's empty leading paragraphs so `- # Heading` blocks don't paste a blank
  // bullet before each heading, and applying the Logseq fold state.
  const json = dropEmptyLeadingParagraphs(parsed.toJSON() as JSONNode);
  if (collapsed.some(Boolean)) applyCollapsed(json, collapsed, { i: 0 });
  const doc = schema.nodeFromJSON(json);

  const blocks: PMNode[] = [];
  doc.forEach((block) => blocks.push(block));
  // A single pasted line of paragraph-shaped text (plain, `- `/`1. ` item, `> ` quote) inserts
  // inline at the caret rather than as a fresh bullet/paragraph that would split the current line.
  // Headings, code blocks, tables, rules, and images keep their block structure.
  if (isSingleLine(markdown)) {
    const inline = soleParagraphInline(blocks);
    if (inline) return new Slice(inline, 0, 0);
  }
  return blocksToSlice(blocks, schema, opts.outline);
}

/**
 * Wrap top-level blocks into a slice ready for `replaceSelection`, shared by the markdown and
 * plain-text paste paths.
 * - document mode (`outline:false`): insert the blocks directly (open depth 0).
 * - outline mode (`outline:true`): every block becomes a list item (a markdown list's own items are
 *   spliced in as siblings), wrapped in a bullet list opened at depth 1 so the items merge into the
 *   surrounding outline at the cursor instead of inserting a nested list.
 */
function blocksToSlice(blocks: PMNode[], schema: Schema, outline: boolean): Slice {
  if (blocks.length === 0) return Slice.empty;

  if (!outline) {
    return new Slice(Fragment.fromArray(blocks), 0, 0);
  }

  const listItemType = schema.nodes.listItem;
  const bulletListType = schema.nodes.bulletList;
  if (!listItemType || !bulletListType) return new Slice(Fragment.fromArray(blocks), 0, 0);

  const items: PMNode[] = [];
  for (const block of blocks) {
    if (block.type.name === 'bulletList' || block.type.name === 'orderedList') {
      // A markdown list's children are already list items — splice them in as siblings.
      block.forEach((item) => items.push(item));
    } else {
      items.push(listItemType.create(null, block));
    }
  }
  if (items.length === 0) return Slice.empty;

  const list = bulletListType.create(null, items);
  // openStart/openEnd = 1 opens the bullet-list boundary so its items merge into the outline
  // at the cursor instead of inserting a nested list.
  return new Slice(Fragment.from(list), 1, 1);
}

/**
 * Turn pasted text into a slice WITHOUT interpreting markdown — the Ctrl/Cmd-Shift-V "paste plain
 * text" path. A single line inserts inline at the caret; each line of a multi-line paste becomes its
 * own block (a bullet in the outline, a paragraph in a document). The characters are inserted
 * verbatim either way, so markdown syntax like `>` quotes, `*`, `#`, and `[](…)` links survive as
 * literal text instead of being parsed into blockquotes/emphasis/etc.
 */
export function plainTextToSlice(
  text: string,
  schema: Schema,
  opts: MarkdownToSliceOptions,
): Slice {
  const paragraphType = schema.nodes.paragraph;
  if (!paragraphType) return Slice.empty;
  // Normalize CRLF/CR to LF, then drop a single trailing newline so a copied block that ends in a
  // line break doesn't paste a stray empty bullet after it.
  const lines = text.replace(/\r\n?/g, '\n').replace(/\n$/, '').split('\n');
  // A single line inserts its characters inline at the caret (still verbatim — no markdown parsing)
  // instead of as a fresh bullet/paragraph that would split the current line.
  if (lines.length === 1 && lines[0]) {
    return new Slice(Fragment.from(schema.text(lines[0])), 0, 0);
  }
  // `schema.text('')` throws, so an empty line becomes an empty paragraph (no text child).
  const blocks = lines.map((line) => paragraphType.create(null, line ? schema.text(line) : null));
  return blocksToSlice(blocks, schema, opts.outline);
}
